/**
 * ExtrasPicker — Sprint T1
 *
 * Buyer "Extras" step: appears in the public event sidebar once ticket
 * quantity > 0. Fixed-price add-ons get compact quantity steppers (capped by
 * max_per_order / availability / ticket count for per_ticket scope);
 * buyer_chooses items (donations, sponsor) get suggested-amount chips + a
 * custom amount input. Entirely optional — never blocks the ticket flow.
 * All amounts are validated server-side; this UI is a convenience layer.
 */
import React from "react";
import { Minus, Plus } from "lucide-react";
import { centsToAUD } from "../lib/events";
import { categoryMeta } from "./AddonsManager";

export default function ExtrasPicker({ addons = [], selection = {}, onChange, totalTickets = 0 }) {
  if (!addons.length) return null;

  const setAddon = (addon, patch) => {
    const cur = selection[addon.id] || { quantity: 0, amount_cents: null };
    const next = { ...cur, ...patch };
    const out = { ...selection };
    if (next.quantity <= 0) delete out[addon.id];
    else out[addon.id] = next;
    onChange(out);
  };

  const capFor = (a) => {
    let cap = a.max_per_order || 10;
    if (a.scope === "per_ticket") cap = Math.min(cap, totalTickets);
    if (a.available != null) cap = Math.min(cap, a.available);
    return Math.max(0, cap);
  };

  return (
    <div className="mt-5 pt-5 border-t border-[var(--gt-line)]" data-testid="extras-picker">
      <div className="gt-label mb-3">Extras (optional)</div>
      <div className="space-y-4">
        {addons.map((a) => {
          const Meta = categoryMeta(a.category);
          const sel = selection[a.id] || { quantity: 0, amount_cents: null };
          const cap = capFor(a);
          const isChoose = a.pricing_type === "buyer_chooses";

          return (
            <div key={a.id} data-testid={`extra-${a.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold flex items-center gap-1.5">
                    <Meta.icon size={13} className="text-[var(--gt-accent)] shrink-0" aria-hidden="true" />
                    {a.name}
                  </div>
                  {a.description && <div className="text-[11px] text-[var(--gt-ink-2)]">{a.description}</div>}
                  <div className="text-[11px] font-mono text-[var(--gt-ink-2)]">
                    {isChoose
                      ? `Any amount from ${centsToAUD(a.min_amount_cents)}`
                      : a.price_cents === 0 ? "Free" : centsToAUD(a.price_cents)}
                    {a.scope === "per_ticket" && " · one per ticket"}
                    {a.available != null && !a.sold_out && a.available <= 5 && (
                      <span className="text-[var(--gt-accent)] font-semibold"> · Limited availability</span>
                    )}
                  </div>
                </div>

                {a.sold_out ? (
                  <span className="text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)] shrink-0">Sold out</span>
                ) : !isChoose ? (
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => setAddon(a, { quantity: Math.max(0, sel.quantity - 1) })}
                      disabled={sel.quantity === 0}
                      className="w-7 h-7 rounded-sm border border-[var(--gt-line)] flex items-center justify-center hover:bg-[var(--gt-bg-surface)] disabled:opacity-40"
                      aria-label={`Remove ${a.name}`}
                      data-testid={`extra-minus-${a.id}`}
                    >
                      <Minus size={12} />
                    </button>
                    <span className="font-mono font-bold text-sm w-5 text-center" data-testid={`extra-qty-${a.id}`}>{sel.quantity}</span>
                    <button
                      type="button"
                      onClick={() => setAddon(a, { quantity: Math.min(cap, sel.quantity + 1) })}
                      disabled={sel.quantity >= cap}
                      className="w-7 h-7 rounded-sm border border-[var(--gt-line)] flex items-center justify-center hover:bg-[var(--gt-bg-surface)] disabled:opacity-40"
                      aria-label={`Add ${a.name}`}
                      data-testid={`extra-plus-${a.id}`}
                    >
                      <Plus size={12} />
                    </button>
                  </div>
                ) : null}
              </div>

              {isChoose && !a.sold_out && (
                <div className="mt-2">
                  <div className="flex flex-wrap gap-1.5">
                    {(a.suggested_amounts_cents || []).map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() =>
                          sel.quantity === 1 && sel.amount_cents === c
                            ? setAddon(a, { quantity: 0, amount_cents: null })
                            : setAddon(a, { quantity: 1, amount_cents: c })
                        }
                        aria-pressed={sel.quantity === 1 && sel.amount_cents === c}
                        className={
                          "px-2.5 py-1.5 rounded-sm border text-[11px] font-mono transition-colors " +
                          (sel.quantity === 1 && sel.amount_cents === c
                            ? "bg-[var(--gt-accent)] text-white border-[var(--gt-accent)]"
                            : "bg-white border-[var(--gt-line)] hover:border-[var(--gt-ink)]")
                        }
                        data-testid={`extra-amount-chip-${a.id}-${c}`}
                      >
                        {centsToAUD(c)}
                      </button>
                    ))}
                    <div className="flex items-center gap-1">
                      <span className="text-[11px] text-[var(--gt-ink-3)]">A$</span>
                      <input
                        type="number"
                        min={a.min_amount_cents / 100}
                        step="1"
                        placeholder="Other"
                        value={sel.quantity === 1 && sel.custom ? sel.customValue ?? "" : ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          const cents = Math.round(parseFloat(v || "0") * 100);
                          if (!v || cents <= 0) setAddon(a, { quantity: 0, amount_cents: null, custom: false, customValue: "" });
                          else setAddon(a, { quantity: 1, amount_cents: cents, custom: true, customValue: v });
                        }}
                        className="w-20 h-8 px-2 rounded-sm border border-[var(--gt-line)] text-[11px] font-mono focus:outline-none focus:border-[var(--gt-ink)]"
                        data-testid={`extra-custom-amount-${a.id}`}
                      />
                    </div>
                  </div>
                  {sel.quantity === 1 && sel.amount_cents != null && sel.amount_cents < a.min_amount_cents && (
                    <div className="mt-1 text-[11px] text-[var(--gt-accent)] font-semibold" data-testid={`extra-min-warning-${a.id}`}>
                      Minimum is {centsToAUD(a.min_amount_cents)}.
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
